import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { motion, useReducedMotion, AnimatePresence } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import {
  Sparkles,
  Play,
  Pause,
  CircleDot,
  KanbanSquare,
  MessagesSquare,
  CheckCircle2,
  Calendar,
  Flag,
  Users,
  Paperclip,
  Smile,
  Send,
  Bell,
  ArrowRight,
  Zap,
  ShieldCheck,
} from "lucide-react";

/**
 * LandingProductTour — full-width animated "video-like" preview.
 * Three auto-cycling scenes (Task Create → Kanban → Chat) with:
 *   • Scene-progress bars (Instagram Stories style)
 *   • Play/Pause toggle
 *   • Clickable scene chips for direct nav
 *   • Reduced-motion fallback (freezes on scene 1, static frames)
 */

const SCENE_DURATION_MS = 6800;

export function LandingProductTour() {
  const { t } = useTranslation();
  const reduced = useReducedMotion();

  const [scene, setScene] = useState(0); // 0,1,2
  const [cycleId, setCycleId] = useState(0); // increments on full cycle → forces fresh mounts
  const [playing, setPlaying] = useState(true);
  const [progress, setProgress] = useState(0); // 0..1 within current scene
  const startedAt = useRef(Date.now());
  const rafRef = useRef(null);

  const scenes = [
    {
      id: "create",
      label: t("landing.tour.chip_create"),
      icon: CircleDot,
    },
    {
      id: "kanban",
      label: t("landing.tour.chip_kanban"),
      icon: KanbanSquare,
    },
    {
      id: "chat",
      label: t("landing.tour.chip_chat"),
      icon: MessagesSquare,
    },
  ];

  // Progress loop
  useEffect(() => {
    if (reduced || !playing) return;
    startedAt.current = Date.now();
    const step = () => {
      const elapsed = Date.now() - startedAt.current;
      const p = Math.min(1, elapsed / SCENE_DURATION_MS);
      setProgress(p);
      if (p >= 1) {
        setScene((s) => {
          const next = (s + 1) % 3;
          // Bump cycleId whenever we wrap back to scene 0 → new mount keys.
          if (next === 0) setCycleId((c) => c + 1);
          return next;
        });
        startedAt.current = Date.now();
        setProgress(0);
      }
      rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => rafRef.current && cancelAnimationFrame(rafRef.current);
  }, [reduced, playing, scene]);

  const jumpTo = (i) => {
    setScene(i);
    setCycleId((c) => c + 1); // force fresh mount even when jumping to same scene id
    setProgress(0);
    startedAt.current = Date.now();
  };

  return (
    <section
      className="relative overflow-hidden bg-gradient-to-b from-white via-violet-50/30 to-white py-20 md:py-28"
      data-testid="landing-product-tour"
    >
      {/* Ambient blur blobs */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -left-24 top-8 h-72 w-72 rounded-full bg-violet-300/25 blur-3xl"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -right-24 bottom-8 h-80 w-80 rounded-full bg-pink-300/25 blur-3xl"
      />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-8">
        {/* Section header */}
        <motion.div
          initial={reduced ? false : { opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
          className="mx-auto max-w-2xl text-center"
        >
          <div
            className="mb-4 inline-flex items-center gap-2 rounded-full border border-violet-200 bg-white/70 px-3 py-1 text-xs font-medium text-violet-700 shadow-sm backdrop-blur"
            data-testid="landing-tour-eyebrow"
          >
            <Sparkles className="h-3 w-3" />
            {t("landing.tour.eyebrow")}
          </div>
          <h2
            className="font-display text-3xl font-extrabold leading-tight tracking-tight text-zinc-950 sm:text-4xl md:text-5xl"
            data-testid="landing-tour-title"
          >
            {t("landing.tour.title_prefix")}
            <span className="serpix-gradient-text">{t("landing.tour.title_accent")}</span>
            {t("landing.tour.title_suffix")}
          </h2>
          <p className="mx-auto mt-4 max-w-xl text-base leading-relaxed text-zinc-600">
            {t("landing.tour.subtitle")}
          </p>
        </motion.div>

        {/* Player container */}
        <motion.div
          initial={reduced ? false : { opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
          className="relative mx-auto mt-12 max-w-6xl"
        >
          {/* Outer soft glow */}
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -inset-6 -z-10 rounded-[36px] bg-gradient-to-br from-violet-500/20 via-fuchsia-400/10 to-pink-500/20 blur-3xl"
          />

          <div className="overflow-hidden rounded-3xl border border-zinc-200 bg-white/95 shadow-2xl shadow-violet-500/15 backdrop-blur">
            {/* Browser chrome + progress bars + chips */}
            <TourChrome
              scenes={scenes}
              current={scene}
              progress={progress}
              playing={playing}
              reduced={reduced}
              onJump={jumpTo}
              onTogglePlay={() => setPlaying((p) => !p)}
              t={t}
            />

            {/* Stage */}
            <div className="relative h-[520px] w-full overflow-hidden bg-gradient-to-br from-zinc-50 via-white to-violet-50/40 sm:h-[560px] md:h-[600px]">
              <AnimatePresence initial={false}>
                {scene === 0 && (
                  <SceneCreate key={`create-${cycleId}`} reduced={reduced} t={t} />
                )}
                {scene === 1 && (
                  <SceneKanban key={`kanban-${cycleId}`} reduced={reduced} t={t} />
                )}
                {scene === 2 && (
                  <SceneChat key={`chat-${cycleId}`} reduced={reduced} t={t} />
                )}
              </AnimatePresence>
            </div>
          </div>

          {/* Caption below player */}
          <div className="mt-6 flex flex-col items-center gap-2 text-center">
            <p className="text-sm text-zinc-500" data-testid="landing-tour-caption">
              {scene === 0 && t("landing.tour.caption_create")}
              {scene === 1 && t("landing.tour.caption_kanban")}
              {scene === 2 && t("landing.tour.caption_chat")}
            </p>
          </div>
        </motion.div>

        {/* Aha-moment CTA banner */}
        <TourCta reduced={reduced} t={t} />
      </div>
    </section>
  );
}

/* ─────────────────── AHA-MOMENT CTA BANNER ─────────────────── */

function TourCta({ reduced, t }) {
  return (
    <motion.div
      initial={reduced ? false : { opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.8, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
      className="relative mx-auto mt-14 max-w-6xl"
      data-testid="landing-tour-cta"
    >
      <div className="relative overflow-hidden rounded-3xl border border-violet-100 bg-gradient-to-br from-white via-violet-50 to-pink-50/60 p-6 shadow-lg shadow-violet-500/10 sm:p-8 md:p-10">
        {/* Ambient orbs */}
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-violet-400/25 blur-3xl"
        />
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -bottom-16 -left-16 h-56 w-56 rounded-full bg-pink-400/25 blur-3xl"
        />

        <div className="relative grid gap-6 md:grid-cols-[1.4fr_1fr] md:items-center md:gap-10">
          {/* Left — copy */}
          <div>
            <div
              className="mb-3 inline-flex items-center gap-1.5 rounded-full border border-violet-200 bg-white/70 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-violet-700 shadow-sm backdrop-blur"
              data-testid="landing-tour-cta-eyebrow"
            >
              <Zap className="h-3 w-3" />
              {t("landing.tour.cta.eyebrow")}
            </div>
            <h3
              className="font-display text-2xl font-extrabold leading-tight tracking-tight text-zinc-950 sm:text-3xl md:text-4xl"
              data-testid="landing-tour-cta-title"
            >
              {t("landing.tour.cta.title_prefix")}
              <span className="serpix-gradient-text">
                {t("landing.tour.cta.title_accent")}
              </span>
              {t("landing.tour.cta.title_suffix")}
            </h3>
            <p className="mt-3 max-w-lg text-sm leading-relaxed text-zinc-600 md:text-base">
              {t("landing.tour.cta.sub")}
            </p>
          </div>

          {/* Right — CTA + trust */}
          <div className="flex flex-col items-start gap-4 md:items-end">
            <Link to="/register" className="w-full sm:w-auto">
              <motion.span
                animate={
                  reduced
                    ? {}
                    : {
                        boxShadow: [
                          "0 10px 24px rgba(124,58,237,0.35)",
                          "0 16px 34px rgba(236,72,153,0.45)",
                          "0 10px 24px rgba(124,58,237,0.35)",
                        ],
                      }
                }
                transition={
                  reduced
                    ? {}
                    : { duration: 2.4, repeat: Infinity, ease: "easeInOut" }
                }
                className="group relative inline-flex w-full items-center justify-center gap-2 overflow-hidden rounded-full bg-gradient-to-r from-[#5B4CFB] to-[#e879a3] px-6 py-3 text-sm font-semibold text-white sm:w-auto sm:text-base"
                data-testid="landing-tour-cta-primary"
              >
                <span className="relative z-10">
                  {t("landing.tour.cta.button")}
                </span>
                <ArrowRight className="relative z-10 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                {/* Shimmer sheen */}
                {!reduced && (
                  <motion.span
                    aria-hidden="true"
                    animate={{ x: ["-120%", "120%"] }}
                    transition={{
                      duration: 2.2,
                      repeat: Infinity,
                      repeatDelay: 1.4,
                      ease: "easeInOut",
                    }}
                    className="absolute inset-y-0 left-0 w-1/3 -skew-x-12 bg-gradient-to-r from-transparent via-white/40 to-transparent"
                  />
                )}
              </motion.span>
            </Link>

            {/* Trust chips */}
            <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11px] font-medium text-zinc-500 md:justify-end">
              <span className="inline-flex items-center gap-1">
                <CheckCircle2 className="h-3 w-3 text-emerald-500" />
                {t("landing.tour.cta.trust_no_card")}
              </span>
              <span className="inline-flex items-center gap-1">
                <Zap className="h-3 w-3 text-amber-500" />
                {t("landing.tour.cta.trust_setup")}
              </span>
              <span className="inline-flex items-center gap-1">
                <ShieldCheck className="h-3 w-3 text-violet-500" />
                {t("landing.tour.cta.trust_isolation")}
              </span>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );
}

/* ─────────────────── CHROME & CONTROLS ─────────────────── */

function TourChrome({ scenes, current, progress, playing, reduced, onJump, onTogglePlay, t }) {
  return (
    <div className="border-b border-zinc-100 bg-white/80 backdrop-blur">
      {/* Traffic lights + URL */}
      <div className="flex items-center gap-3 px-5 py-3">
        <div className="flex gap-1.5">
          <span className="h-3 w-3 rounded-full bg-red-400" />
          <span className="h-3 w-3 rounded-full bg-amber-400" />
          <span className="h-3 w-3 rounded-full bg-emerald-400" />
        </div>
        <div className="mx-auto flex max-w-md flex-1 items-center gap-2 rounded-full border border-zinc-200 bg-zinc-50 px-3 py-1 text-[11px] text-zinc-500">
          <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
          serpix.co / <span className="text-zinc-900 font-medium">{scenes[current].label.toLowerCase()}</span>
        </div>
        <button
          onClick={onTogglePlay}
          data-testid="landing-tour-play-toggle"
          className="flex h-8 w-8 items-center justify-center rounded-full border border-zinc-200 bg-white text-zinc-600 shadow-sm transition-colors hover:bg-zinc-50 hover:text-zinc-900"
          aria-label={playing ? t("landing.tour.pause") : t("landing.tour.play")}
        >
          {playing ? <Pause className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
        </button>
      </div>

      {/* Scene picker chips + progress bars */}
      <div className="grid grid-cols-3 gap-0 border-t border-zinc-100">
        {scenes.map((s, i) => {
          const Icon = s.icon;
          const active = i === current;
          return (
            <button
              key={s.id}
              onClick={() => onJump(i)}
              data-testid={`landing-tour-chip-${s.id}`}
              className={
                "group relative flex flex-col items-center gap-1.5 px-3 py-3 text-[11px] font-medium transition-colors sm:flex-row sm:justify-center sm:gap-2 sm:text-xs " +
                (active
                  ? "bg-violet-50/60 text-violet-800"
                  : "text-zinc-500 hover:bg-zinc-50 hover:text-zinc-800")
              }
            >
              <Icon
                className={
                  "h-3.5 w-3.5 " + (active ? "text-violet-600" : "text-zinc-400")
                }
              />
              <span className="truncate">{s.label}</span>
              {/* Progress rail */}
              <span className="absolute bottom-0 left-0 right-0 h-[3px] overflow-hidden bg-zinc-100">
                <motion.span
                  className={
                    "block h-full " +
                    (active
                      ? "bg-gradient-to-r from-violet-500 to-pink-500"
                      : i < current
                        ? "bg-violet-300/60"
                        : "bg-transparent")
                  }
                  animate={{
                    width: active
                      ? `${progress * 100}%`
                      : i < current
                        ? "100%"
                        : "0%",
                  }}
                  transition={{ duration: reduced ? 0 : 0.1, ease: "linear" }}
                />
              </span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

/* ─────────────────── SCENE 1 — TASK CREATE ─────────────────── */

const CREATE_TITLE = "Yeni landing hero'yu tasarla";
const CREATE_STEPS = [
  { at: 0.05, key: "type" }, // typewriter
  { at: 0.45, key: "priority" }, // priority selected
  { at: 0.6, key: "assign" }, // assignees appear
  { at: 0.75, key: "due" }, // due date pill
  { at: 0.9, key: "create" }, // button press glow
];

function SceneCreate({ reduced, t }) {
  const [typed, setTyped] = useState(reduced ? CREATE_TITLE : "");
  const [step, setStep] = useState(reduced ? 4 : -1);

  useEffect(() => {
    if (reduced) return;
    setTyped("");
    setStep(-1);
    let i = 0;
    const typer = setInterval(() => {
      i += 1;
      setTyped(CREATE_TITLE.slice(0, i));
      if (i >= CREATE_TITLE.length) clearInterval(typer);
    }, 55);
    const timers = CREATE_STEPS.map((_, idx) =>
      setTimeout(() => setStep(idx), 1900 + idx * 800),
    );
    return () => {
      clearInterval(typer);
      timers.forEach((tm) => clearTimeout(tm));
    };
  }, [reduced]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      className="absolute inset-0"
    >
      {/* Blurred workspace behind */}
      <BlurredWorkspace />

      {/* Dark backdrop */}
      <motion.div
        initial={reduced ? { opacity: 0.45 } : { opacity: 0 }}
        animate={{ opacity: 0.45 }}
        transition={{ duration: 0.6 }}
        className="absolute inset-0 bg-zinc-950"
      />

      {/* Modal */}
      <div className="absolute inset-0 flex items-center justify-center px-4 sm:px-8">
        <motion.div
          initial={reduced ? false : { opacity: 0, y: 30, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="w-full max-w-lg overflow-hidden rounded-2xl border border-zinc-200 bg-white shadow-2xl shadow-violet-500/25"
        >
          {/* Modal header */}
          <div className="flex items-center justify-between border-b border-zinc-100 px-5 py-3">
            <div className="flex items-center gap-2">
              <div className="flex h-6 w-6 items-center justify-center rounded-lg bg-violet-100 text-violet-700">
                <CircleDot className="h-3.5 w-3.5" />
              </div>
              <span className="text-sm font-semibold text-zinc-900">
                {t("landing.tour.create.modal_title")}
              </span>
            </div>
            <span className="rounded bg-zinc-100 px-1.5 py-0.5 text-[10px] font-medium text-zinc-500">
              Esc
            </span>
          </div>

          {/* Body */}
          <div className="space-y-5 px-5 py-5">
            {/* Title input with typewriter */}
            <div>
              <label className="mb-1.5 block text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
                {t("landing.tour.create.title_label")}
              </label>
              <div className="relative rounded-xl border-2 border-violet-400 bg-white px-3 py-2.5 shadow-sm shadow-violet-300/40">
                <div className="min-h-[20px] text-sm font-medium text-zinc-900">
                  {typed}
                  {!reduced && typed.length < CREATE_TITLE.length && (
                    <motion.span
                      animate={{ opacity: [1, 0, 1] }}
                      transition={{ duration: 0.9, repeat: Infinity }}
                      className="ml-0.5 inline-block h-[14px] w-[2px] translate-y-[2px] bg-violet-500 align-middle"
                    />
                  )}
                </div>
              </div>
            </div>

            {/* Priority chips */}
            <div>
              <div className="mb-1.5 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
                <Flag className="h-3 w-3" />
                {t("landing.tour.create.priority_label")}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {[
                  { k: "low", cls: "bg-zinc-100 text-zinc-600" },
                  { k: "med", cls: "bg-sky-100 text-sky-700" },
                  { k: "high", cls: "bg-amber-100 text-amber-700" },
                  { k: "urg", cls: "bg-red-100 text-red-700" },
                ].map((p) => {
                  const selected = step >= 1 && p.k === "high";
                  return (
                    <motion.span
                      key={p.k}
                      animate={selected ? { scale: [1, 1.15, 1] } : { scale: 1 }}
                      transition={{ duration: 0.4 }}
                      className={
                        "rounded-full px-2.5 py-1 text-[11px] font-semibold transition-all " +
                        p.cls +
                        (selected
                          ? " ring-2 ring-amber-400 ring-offset-1"
                          : step >= 1
                            ? " opacity-40"
                            : "")
                      }
                    >
                      {t(`landing.tour.create.priority.${p.k}`)}
                    </motion.span>
                  );
                })}
              </div>
            </div>

            {/* Assignees */}
            <div>
              <div className="mb-1.5 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
                <Users className="h-3 w-3" />
                {t("landing.tour.create.assignees_label")}
              </div>
              <div className="flex items-center gap-1.5">
                {[
                  { init: "AY", cls: "bg-violet-500" },
                  { init: "EK", cls: "bg-pink-500" },
                  { init: "MT", cls: "bg-amber-500" },
                ].map((a, idx) => (
                  <motion.div
                    key={a.init}
                    initial={reduced ? false : { scale: 0, opacity: 0 }}
                    animate={
                      step >= 2
                        ? { scale: 1, opacity: 1 }
                        : { scale: 0, opacity: 0 }
                    }
                    transition={{
                      delay: idx * 0.12,
                      type: "spring",
                      stiffness: 380,
                      damping: 20,
                    }}
                    className={
                      "flex h-7 w-7 items-center justify-center rounded-full border-2 border-white text-[10px] font-bold text-white shadow-md " +
                      a.cls
                    }
                    style={{ marginLeft: idx > 0 ? "-6px" : 0 }}
                  >
                    {a.init}
                  </motion.div>
                ))}
                <motion.div
                  initial={reduced ? false : { scale: 0, opacity: 0 }}
                  animate={
                    step >= 2
                      ? { scale: 1, opacity: 1 }
                      : { scale: 0, opacity: 0 }
                  }
                  transition={{ delay: 0.4, duration: 0.3 }}
                  className="ml-1 flex h-7 items-center rounded-full border border-dashed border-zinc-300 px-2 text-[10px] font-medium text-zinc-500"
                >
                  +2
                </motion.div>
              </div>
            </div>

            {/* Due date pill */}
            <motion.div
              initial={reduced ? false : { opacity: 0, y: 8 }}
              animate={step >= 3 ? { opacity: 1, y: 0 } : { opacity: 0, y: 8 }}
              transition={{ duration: 0.4 }}
              className="flex items-center gap-2"
            >
              <div className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold text-emerald-700">
                <Calendar className="h-3 w-3" />
                {t("landing.tour.create.due_pill")}
              </div>
              <div className="inline-flex items-center gap-1 text-[11px] text-zinc-500">
                <Paperclip className="h-3 w-3" />
                figma.link
              </div>
            </motion.div>
          </div>

          {/* Footer / button */}
          <div className="flex items-center justify-end gap-2 border-t border-zinc-100 bg-zinc-50/60 px-5 py-3">
            <button className="rounded-lg px-3 py-1.5 text-xs font-medium text-zinc-500 hover:text-zinc-800">
              {t("landing.tour.create.cancel")}
            </button>
            <motion.button
              animate={
                !reduced && step >= 4
                  ? {
                      scale: [1, 1.06, 1],
                      boxShadow: [
                        "0 8px 20px rgba(124,58,237,0.35)",
                        "0 14px 30px rgba(236,72,153,0.5)",
                        "0 8px 20px rgba(124,58,237,0.35)",
                      ],
                    }
                  : {}
              }
              transition={{ duration: 1.2, repeat: Infinity, ease: "easeInOut" }}
              className="inline-flex items-center gap-1.5 rounded-lg bg-gradient-to-r from-violet-600 to-pink-500 px-3 py-1.5 text-xs font-semibold text-white shadow-lg shadow-violet-500/40"
            >
              <Send className="h-3 w-3" />
              {t("landing.tour.create.submit")}
            </motion.button>
          </div>
        </motion.div>
      </div>

      {/* Ping ripple after "create" */}
      <AnimatePresence>
        {!reduced && step >= 4 && (
          <motion.div
            initial={{ opacity: 0.6, scale: 0.8 }}
            animate={{ opacity: 0, scale: 2 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.6, repeat: Infinity, ease: "easeOut" }}
            className="pointer-events-none absolute bottom-24 right-1/2 h-24 w-24 translate-x-1/2 rounded-full bg-violet-400/40"
          />
        )}
      </AnimatePresence>
    </motion.div>
  );
}

/* ─────────────────── SCENE 2 — KANBAN ─────────────────── */

const KANBAN_COLS = [
  { id: "todo", key: "todo", accent: "text-zinc-600 bg-zinc-100" },
  { id: "prog", key: "in_progress", accent: "text-sky-700 bg-sky-100" },
  { id: "done", key: "done", accent: "text-emerald-700 bg-emerald-100" },
];

const KANBAN_STATIC = {
  todo: [
    { title: "Faturalama akışını gözden geçir", tag: "high" },
    { title: "Yeni sponsor pitchi", tag: "med" },
  ],
  prog: [
    { title: "Motivasyon rozetleri v2", tag: "med" },
  ],
  done: [
    { title: "Landing metin revize", tag: "low" },
    { title: "Push abonelik migrasyonu", tag: "high" },
  ],
};

function SceneKanban({ reduced, t }) {
  // Highlighted card starts in "todo", moves to "prog", then "done"
  const [pos, setPos] = useState(0); // 0=todo,1=prog,2=done

  useEffect(() => {
    if (reduced) {
      setPos(2);
      return;
    }
    setPos(0);
    const t1 = setTimeout(() => setPos(1), 1800);
    const t2 = setTimeout(() => setPos(2), 4200);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [reduced]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
      className="absolute inset-0 flex flex-col"
    >
      {/* Fake app header */}
      <div className="flex items-center justify-between border-b border-zinc-100 bg-white/60 px-5 py-3 backdrop-blur">
        <div className="flex items-center gap-2">
          <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-violet-100 text-violet-700">
            <KanbanSquare className="h-3.5 w-3.5" />
          </div>
          <span className="text-sm font-semibold text-zinc-900">
            {t("landing.tour.kanban.project")}
          </span>
          <span className="text-[11px] text-zinc-500">·</span>
          <span className="text-[11px] text-zinc-500">
            {t("landing.tour.kanban.board_view")}
          </span>
        </div>
        <div className="hidden gap-1.5 sm:flex">
          {["AY", "EK", "MT", "SB"].map((n, i) => (
            <div
              key={n}
              className={
                "flex h-6 w-6 items-center justify-center rounded-full border-2 border-white text-[9px] font-bold text-white shadow-sm " +
                ["bg-violet-500", "bg-pink-500", "bg-amber-500", "bg-emerald-500"][i]
              }
              style={{ marginLeft: i > 0 ? "-6px" : 0 }}
            >
              {n}
            </div>
          ))}
        </div>
      </div>

      {/* Board */}
      <div className="grid flex-1 grid-cols-3 gap-3 p-4 md:gap-4 md:p-6">
        {KANBAN_COLS.map((col, colIdx) => {
          const staticCards = KANBAN_STATIC[col.id];
          const cardHere = pos === colIdx;
          const liveCount =
            staticCards.length + (cardHere ? 1 : 0);
          return (
            <div key={col.id} className="flex min-h-0 flex-col">
              {/* Column header */}
              <div className="mb-2 flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <span
                    className={
                      "inline-block h-1.5 w-1.5 rounded-full " +
                      ["bg-zinc-400", "bg-sky-500", "bg-emerald-500"][colIdx]
                    }
                  />
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-zinc-600">
                    {t(`landing.tour.kanban.status_${col.key}`)}
                  </span>
                </div>
                <motion.span
                  key={liveCount}
                  initial={reduced ? false : { scale: 1.3, color: "#7c3aed" }}
                  animate={{ scale: 1, color: "#71717a" }}
                  transition={{ duration: 0.3 }}
                  className={
                    "rounded-full px-1.5 py-0.5 text-[10px] font-bold " + col.accent
                  }
                >
                  {liveCount}
                </motion.span>
              </div>

              {/* Cards zone */}
              <div className="flex-1 space-y-2 rounded-2xl border border-dashed border-zinc-200 bg-white/70 p-2 backdrop-blur">
                {/* Highlighted moving card */}
                {cardHere && (
                  <motion.div
                    layoutId="kanban-hero-card"
                    transition={{
                      layout: {
                        duration: reduced ? 0 : 0.85,
                        type: "spring",
                        stiffness: 260,
                        damping: 28,
                      },
                    }}
                    className="relative overflow-hidden rounded-xl border-2 border-violet-400 bg-gradient-to-br from-white via-violet-50 to-pink-50 p-2.5 shadow-lg shadow-violet-500/25"
                  >
                    <div className="mb-1 flex items-center justify-between">
                      <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[9px] font-bold uppercase text-amber-700">
                        {t("landing.tour.kanban.tag_high")}
                      </span>
                      <div className="flex h-5 w-5 items-center justify-center rounded-full bg-violet-500 text-[9px] font-bold text-white">
                        AY
                      </div>
                    </div>
                    <div className="text-[11px] font-semibold text-zinc-900">
                      Yeni landing hero&apos;yu tasarla
                    </div>
                    <div className="mt-1.5 flex items-center gap-2 text-[10px] text-zinc-500">
                      <span className="inline-flex items-center gap-0.5">
                        <Calendar className="h-2.5 w-2.5" />
                        14 Şub
                      </span>
                      <span className="inline-flex items-center gap-0.5">
                        <CheckCircle2 className="h-2.5 w-2.5" />
                        2/5
                      </span>
                    </div>
                    {/* Progress underline */}
                    <div className="mt-1.5 h-1 w-full overflow-hidden rounded-full bg-white/70">
                      <motion.div
                        initial={{ width: "0%" }}
                        animate={{
                          width:
                            colIdx === 0
                              ? "20%"
                              : colIdx === 1
                                ? "60%"
                                : "100%",
                        }}
                        transition={{ duration: 0.8, ease: "easeOut" }}
                        className={
                          "h-full rounded-full " +
                          [
                            "bg-zinc-400",
                            "bg-sky-500",
                            "bg-gradient-to-r from-violet-500 to-pink-500",
                          ][colIdx]
                        }
                      />
                    </div>
                  </motion.div>
                )}
                {/* Static cards */}
                {staticCards.map((card) => (
                  <div
                    key={card.title}
                    className="rounded-xl border border-zinc-200 bg-white p-2.5 shadow-sm"
                  >
                    <div className="mb-1 flex items-center justify-between">
                      <TagBadge tag={card.tag} t={t} />
                      <div className="flex h-5 w-5 items-center justify-center rounded-full bg-zinc-200 text-[9px] font-bold text-zinc-600">
                        EK
                      </div>
                    </div>
                    <div className="text-[11px] font-medium text-zinc-800">
                      {card.title}
                    </div>
                  </div>
                ))}
                {/* Empty-state placeholder */}
                {staticCards.length === 0 && !cardHere && (
                  <div className="rounded-xl border border-dashed border-zinc-200 py-4 text-center text-[10px] text-zinc-400">
                    {t("landing.tour.kanban.empty")}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {/* Confetti burst when reaching "done" */}
      <AnimatePresence>
        {!reduced && pos === 2 && (
          <motion.div
            key="confetti"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="pointer-events-none absolute inset-x-0 bottom-6 flex items-center justify-center"
          >
            <ConfettiBurst />
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

function TagBadge({ tag, t }) {
  const map = {
    urg: "bg-red-100 text-red-700",
    high: "bg-amber-100 text-amber-700",
    med: "bg-sky-100 text-sky-700",
    low: "bg-zinc-100 text-zinc-500",
  };
  return (
    <span
      className={
        "rounded px-1.5 py-0.5 text-[9px] font-bold uppercase " + map[tag]
      }
    >
      {t(`landing.tour.kanban.tag_${tag}`)}
    </span>
  );
}

function ConfettiBurst() {
  const dots = Array.from({ length: 14 });
  const colors = [
    "#7c3aed",
    "#ec4899",
    "#f59e0b",
    "#10b981",
    "#3b82f6",
  ];
  return (
    <div className="relative h-8 w-64">
      {dots.map((_, i) => {
        const dx = (Math.random() - 0.5) * 220;
        const dy = -(60 + Math.random() * 60);
        return (
          <motion.span
            key={i}
            initial={{ x: 0, y: 0, opacity: 1, rotate: 0 }}
            animate={{
              x: dx,
              y: dy,
              opacity: 0,
              rotate: (Math.random() - 0.5) * 360,
            }}
            transition={{ duration: 1.4, ease: "easeOut", delay: i * 0.03 }}
            className="absolute left-1/2 top-1/2 h-1.5 w-1.5 rounded-sm"
            style={{ background: colors[i % colors.length] }}
          />
        );
      })}
    </div>
  );
}

/* ─────────────────── SCENE 3 — CHAT + PUSH ─────────────────── */

const CHAT_MESSAGES = [
  {
    from: "them",
    name: "Elif Kaya",
    initials: "EK",
    color: "bg-pink-500",
    text: "Yeni landing hero'yu bitirdim — bir bakabilir misin? 🚀",
    at: 0.05,
    reactions: [{ e: "👍", c: 2, at: 0.5 }],
  },
  {
    from: "me",
    name: "Ali",
    text: "Şahane olmuş — motion detayları çok temiz.",
    at: 0.35,
  },
  {
    from: "them",
    name: "Mert T.",
    initials: "MT",
    color: "bg-amber-500",
    text: "Toplantı 14:00'te başlıyor — herkese yeni görev düşecek.",
    at: 0.6,
    push: true,
  },
];

function SceneChat({ reduced, t }) {
  const [visible, setVisible] = useState(reduced ? CHAT_MESSAGES.length : 0);
  const [typing, setTyping] = useState(false);
  const [reactionOn, setReactionOn] = useState(reduced);
  const [pushOn, setPushOn] = useState(reduced);

  useEffect(() => {
    if (reduced) return;
    setVisible(0);
    setTyping(false);
    setReactionOn(false);
    setPushOn(false);

    const timers = [];
    // Show message 1 (them) after 0.4s
    timers.push(setTimeout(() => setVisible(1), 400));
    // Show reaction on m1 at 2.5s
    timers.push(setTimeout(() => setReactionOn(true), 2500));
    // Typing indicator at 1.6s → 2.6s
    timers.push(setTimeout(() => setTyping(true), 1600));
    timers.push(setTimeout(() => setTyping(false), 2600));
    // Show message 2 (me) at 2.7s
    timers.push(setTimeout(() => setVisible(2), 2700));
    // Show message 3 (them, push) at 4.4s
    timers.push(setTimeout(() => setVisible(3), 4400));
    // Push bubble at 4.8s
    timers.push(setTimeout(() => setPushOn(true), 4800));

    return () => timers.forEach((tm) => clearTimeout(tm));
  }, [reduced]);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
      className="absolute inset-0 flex flex-col"
    >
      {/* Channel header */}
      <div className="flex items-center justify-between border-b border-zinc-100 bg-white/80 px-5 py-3 backdrop-blur">
        <div className="flex items-center gap-2">
          <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-violet-100 text-violet-700 font-semibold">
            #
          </div>
          <div>
            <div className="text-sm font-semibold text-zinc-900">
              {t("landing.tour.chat.channel")}
            </div>
            <div className="text-[10px] text-zinc-500">
              {t("landing.tour.chat.channel_meta")}
            </div>
          </div>
        </div>
        <div className="hidden items-center gap-1.5 sm:flex">
          <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
            ● online
          </span>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 space-y-3 overflow-hidden px-4 py-4 sm:px-6 md:px-10">
        {CHAT_MESSAGES.map((msg, idx) => {
          if (visible <= idx) return null;
          const mine = msg.from === "me";
          return (
            <motion.div
              key={idx}
              initial={
                reduced
                  ? false
                  : { opacity: 0, y: 10, x: mine ? 30 : -30 }
              }
              animate={{ opacity: 1, y: 0, x: 0 }}
              transition={{
                duration: 0.5,
                ease: [0.22, 1, 0.36, 1],
              }}
              className={
                "flex items-end gap-2 " + (mine ? "justify-end" : "justify-start")
              }
            >
              {!mine && (
                <div
                  className={
                    "flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[10px] font-bold text-white shadow-sm " +
                    msg.color
                  }
                >
                  {msg.initials}
                </div>
              )}
              <div className={"max-w-[70%] " + (mine ? "text-right" : "text-left")}>
                {!mine && (
                  <div className="mb-0.5 flex items-center gap-1.5">
                    <span className="text-[11px] font-semibold text-zinc-900">
                      {msg.name}
                    </span>
                    <span className="text-[10px] text-zinc-400">şimdi</span>
                  </div>
                )}
                <div className="relative inline-block">
                  <div
                    className={
                      "rounded-2xl px-3 py-2 text-[12px] shadow-sm " +
                      (mine
                        ? "bg-gradient-to-br from-violet-600 to-pink-500 text-white"
                        : "border border-zinc-200 bg-white text-zinc-800")
                    }
                  >
                    {msg.text}
                  </div>
                  {/* Reactions */}
                  {!mine && msg.reactions && reactionOn && (
                    <motion.div
                      initial={reduced ? false : { scale: 0, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{
                        type: "spring",
                        stiffness: 380,
                        damping: 20,
                      }}
                      className="mt-1 flex gap-1"
                    >
                      {msg.reactions.map((r) => (
                        <span
                          key={r.e}
                          className="inline-flex items-center gap-1 rounded-full border border-zinc-200 bg-white px-1.5 py-0.5 text-[10px] shadow-sm"
                        >
                          <span>{r.e}</span>
                          <span className="font-semibold text-zinc-600">
                            {r.c}
                          </span>
                        </span>
                      ))}
                    </motion.div>
                  )}
                </div>
              </div>
            </motion.div>
          );
        })}

        {/* Typing indicator */}
        <AnimatePresence>
          {typing && !reduced && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 8 }}
              transition={{ duration: 0.25 }}
              className="flex items-end gap-2"
            >
              <div className="flex h-7 w-7 items-center justify-center rounded-full bg-zinc-300 text-[10px] font-bold text-white">
                A
              </div>
              <div className="rounded-2xl border border-zinc-200 bg-white px-3 py-2 shadow-sm">
                <div className="flex gap-1">
                  {[0, 1, 2].map((i) => (
                    <motion.span
                      key={i}
                      animate={{ y: [0, -3, 0] }}
                      transition={{
                        duration: 0.8,
                        repeat: Infinity,
                        delay: i * 0.15,
                        ease: "easeInOut",
                      }}
                      className="block h-1.5 w-1.5 rounded-full bg-zinc-400"
                    />
                  ))}
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Composer */}
      <div className="border-t border-zinc-100 bg-white/70 p-3 backdrop-blur">
        <div className="mx-auto flex max-w-3xl items-center gap-2 rounded-2xl border border-zinc-200 bg-white px-3 py-2 shadow-sm">
          <Smile className="h-4 w-4 text-zinc-400" />
          <Paperclip className="h-4 w-4 text-zinc-400" />
          <div className="flex-1 text-[12px] text-zinc-400">
            {t("landing.tour.chat.composer")}
          </div>
          <button className="flex h-7 w-7 items-center justify-center rounded-lg bg-gradient-to-br from-violet-600 to-pink-500 text-white">
            <Send className="h-3.5 w-3.5" />
          </button>
        </div>
      </div>

      {/* Floating push bubble */}
      <AnimatePresence>
        {pushOn && (
          <motion.div
            key="push"
            initial={reduced ? false : { opacity: 0, y: -30, x: 30 }}
            animate={{ opacity: 1, y: 0, x: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="absolute right-4 top-16 w-[240px] rounded-2xl border border-zinc-200 bg-white/95 p-3 shadow-2xl shadow-violet-500/25 backdrop-blur"
            data-testid="landing-tour-push-bubble"
          >
            <div className="mb-1.5 flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                <span className="inline-flex h-4 w-4 items-center justify-center rounded-[5px] bg-violet-600 text-[8px] font-bold text-white">
                  S
                </span>
                <span className="text-[9px] font-semibold uppercase tracking-wide text-zinc-500">
                  Serpix
                </span>
              </div>
              <span className="inline-flex items-center gap-0.5 text-[9px] text-zinc-400">
                <Bell className="h-2.5 w-2.5" />
                şimdi
              </span>
            </div>
            <div className="text-[11px] font-semibold text-zinc-900">
              Mert T.
            </div>
            <div className="text-[10px] leading-snug text-zinc-600">
              Toplantı 14:00&apos;te başlıyor — herkese yeni görev düşecek.
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

/* ─────────────────── SHARED — BLURRED WORKSPACE BG ─────────────────── */

function BlurredWorkspace() {
  return (
    <div
      aria-hidden="true"
      className="absolute inset-0 opacity-60 blur-[3px]"
    >
      <div className="flex h-full">
        {/* Sidebar */}
        <div className="w-40 border-r border-zinc-100 bg-zinc-50/70 p-3 space-y-2">
          {["Dashboard", "Projeler", "Takvim", "Motivasyon", "Ayarlar"].map(
            (l, i) => (
              <div
                key={l}
                className={
                  "rounded-lg px-2 py-1.5 text-[11px] " +
                  (i === 1
                    ? "bg-violet-100 font-semibold text-violet-700"
                    : "text-zinc-500")
                }
              >
                {l}
              </div>
            ),
          )}
        </div>
        {/* Body */}
        <div className="flex-1 space-y-3 p-6">
          {[70, 40, 55, 30, 60].map((w, i) => (
            <div key={i} className="space-y-1.5">
              <div className="h-2 rounded-full bg-zinc-200" style={{ width: `${w}%` }} />
              <div className="h-2 rounded-full bg-zinc-100" style={{ width: `${w - 15}%` }} />
            </div>
          ))}
          <div className="mt-6 grid grid-cols-3 gap-3">
            {[0, 1, 2].map((c) => (
              <div key={c} className="h-24 rounded-xl bg-white/80 shadow-sm" />
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
